import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { MemberRef } from "./member";

/**
 * Dataset 전체 통계다. organization 스코프로 집계한다(분포·총량·수집 추이·라벨).
 *
 * 단건 데이터셋 진단(`datasetTrainingOverview`)과 분리된 read-only 개요 계약이다. 서버가 모든
 * 축을 고정 개수의 집계 쿼리로 계산해 한 번에 반환한다. 그래서 FE 는 축마다 호출하지 않는다.
 *
 * 이 모듈의 모든 축·총량·추이·라벨의 모집단은 available 데이터셋이다(데이터셋 목록 기본값·라벨
 * 카탈로그와 같은 기준). 응답의 `status_counts`(전체 lifecycle 집계)는 화면이 쓰지 않는다.
 * 그래서 타입에 넣지 않는다. FE 는 쓰는 필드만 모델링한다.
 */

/** 수집 추이의 시간 버킷. 서버 `date_trunc` 단위와 같은 어휘다. */
export type DatasetAnalyticsTrendBucket = "day" | "week";

/** 한 축 값의 데이터셋 수다. `value: null` 은 미판정(NULL) 묶음이다. 0 이 아니다. */
export type DatasetAnalyticsValueCount = {
  value: string | null;
  count: number;
};

/** 카메라 스트림 수별 데이터셋 수다. `value: null` 은 summary 를 계산하지 않은 묶음이다. */
export type DatasetAnalyticsCameraStreamCount = {
  value: number | null;
  count: number;
};

/** operator 별 수집량이다. `member: null` 은 담당자 미지정 묶음이다. */
export type DatasetAnalyticsOperatorCount = {
  member: MemberRef | null;
  count: number;
};

/**
 * 수집 추이의 한 버킷이다. 버킷 시작 시각(UTC)과 그 버킷의 생성 수·볼륨 합계를 담는다.
 *
 * 볼륨 3필드는 그 버킷에 생성된 데이터셋들의 summary 합이다. 그래서 전체 합이 totals 와 같다.
 * summary 를 계산하지 않은 데이터셋은 서버가 0 으로 센다. 그래서 null 이 없다.
 */
export type DatasetAnalyticsTrendPoint = {
  bucket_start: string;
  count: number;
  episode_count: number;
  total_duration_sec: number;
  total_size_bytes: number;
};

/** float 지표의 분위 요약이다. 표본이 하나도 없으면 다섯 필드가 모두 null 이다. */
export type DatasetAnalyticsNumericSummary = {
  min: number | null;
  p25: number | null;
  p50: number | null;
  p75: number | null;
  max: number | null;
};

/** available 모집단의 총량이다. `total_duration_sec` 은 초, `total_size_bytes` 는 바이트다. */
export type DatasetAnalyticsTotals = {
  dataset_count: number;
  reviewed_count: number;
  episode_count: number;
  total_duration_sec: number;
  total_size_bytes: number;
};

/** 예약 라벨 key(task/environment/source)의 값별 부여 수다. */
export type DatasetAnalyticsLabelAxes = {
  task: DatasetAnalyticsValueCount[];
  environment: DatasetAnalyticsValueCount[];
  source: DatasetAnalyticsValueCount[];
};

/**
 * task 라벨 값 × embodiment 이름 교차의 데이터셋 수다.
 *
 * task 라벨이 없는 데이터셋은 빠진다. 한 데이터셋이 task 라벨을 여러 개 가질 수 있다.
 * 그래서 셀 합이 dataset_count 보다 클 수 있다.
 */
export type DatasetAnalyticsTaskEmbodimentCell = {
  task: string;
  embodiment: string;
  count: number;
};

/**
 * `GET /v1/datasets/analytics` 응답.
 *
 * 모든 `*_counts` 는 **count 내림차순**이다. 배열 순서는 데이터에 따라 바뀐다. 그래서 화면은
 * 색을 인덱스에 연결하지 않는다. 색은 값에 연결한다.
 */
export type DatasetAnalyticsResp = {
  totals: DatasetAnalyticsTotals;
  embodiment_counts: DatasetAnalyticsValueCount[];
  tip_counts: DatasetAnalyticsValueCount[];
  recording_mode_counts: DatasetAnalyticsValueCount[];
  data_origin_counts: DatasetAnalyticsValueCount[];
  arm_layout_counts: DatasetAnalyticsValueCount[];
  camera_stream_counts: DatasetAnalyticsCameraStreamCount[];
  operator_counts: DatasetAnalyticsOperatorCount[];
  trend_bucket: DatasetAnalyticsTrendBucket;
  // 값이 있는 버킷만 들어 있다. 서버는 빈 구간을 채우지 않는다. 화면이 빈 구간을 0 으로
  // 채워야 추이가 왜곡되지 않는다.
  created_trend: DatasetAnalyticsTrendPoint[];
  episode_length_sec: DatasetAnalyticsNumericSummary;
  primary_rate_hz: DatasetAnalyticsNumericSummary;
  labels: DatasetAnalyticsLabelAxes;
  // 정렬은 count desc → task asc → embodiment asc 다. 접기와 정규화는 화면 모델이 한다.
  task_embodiment_matrix: DatasetAnalyticsTaskEmbodimentCell[];
};

/**
 * organization 데이터셋 전체 통계를 조회한다.
 *
 * `bucket` 은 수집 추이의 버킷 단위만 정한다. 다른 축의 값은 버킷과 관계없이 같다.
 */
export async function getDatasetAnalytics(
  bucket: DatasetAnalyticsTrendBucket = "week",
): Promise<DatasetAnalyticsResp> {
  const response = await apiClient.get<DatasetAnalyticsResp>(apiRoutes.dataset.analytics, {
    params: { bucket },
  });
  return response.data;
}
